import { store, storeActions, entityStore } from '~/utility/BinSentry-ui-utility';
import { StoreMixin } from '../../utility/store-mixin';
import { LitEntity, html, css, renderIf } from '../../utility/lit-entity';
import { SirenActionMixin } from '../../utility/siren-action-mixin';
import { sharedStyles } from '../../styles/shared-styles';
import { bannerStyles } from '../../styles/banner-styles';
import '../../bins/parts/bin-path-name';
import '../../bins/parts/bin-photo';
import '../../bins/parts/bin-location';
import '../../common/bn-flash';
import '../../common/bn-surface';
import './work-order-location-details';

class WorkOrderBinConfirm extends SirenActionMixin(StoreMixin(LitEntity)) {
  static get properties() {
    return {
      binHref: { type: String, attribute: false },
      photoHref: { type: String, attribute: false },
      serialNumber: { type: String, attribute: false },
      canSkip: { type: Boolean, attribute: false },
      replacing: { type: Boolean, attribute: false },
    };
  }

  static get styles() {
    return [sharedStyles, bannerStyles, css`
      h1 {
        display: flex;
        align-items: center;
        gap: 0.5rem;
      }
      .heading {
        margin-left: var(--root-margin);
      }
      .content {
        display: flex;
        flex-wrap: wrap;
        justify-content: stretch;
      }
      .content bn-surface {
        flex-grow: 1;
      }
      .content bn-surface.location {
        flex-grow: 2;
        display: flex;
        flex-direction: column;
      }
      .location bin-location {
        flex-grow: 1;
        width: 100%;
        min-width: 200px;
        min-height: 300px;
      }
      .photo {
        text-align: center;
        background-color: var(--gray-20);
      }
      img {
        width: 100%;
        max-width: 400px;
      }
      .skip {
        padding: 1rem;
        text-align: center;
      }
    `];
  }

  renderEntity() {
    const locationHref = this.getHref('https://api.binsentry.com/rel/work-order-location');

    return html`
      <lit-entity .href="${this.binHref}" @update="${this._onUpdateBin}"></lit-entity>

      <div>
        <h1>
          <a href="/work-order-schedule/work-orders?locationId=${encodeURIComponent(locationHref)}">
            <button class="button button--outlined">
              <bn-icon name="far fa-less-than"></bn-icon>
            </button>
          </a>
          Work Order #${this.getProperty('referenceId')}
        </h1>
      </div>

      <div class="heading">
        <h2>Bin Confirmation</h2>
        <h3><bin-path-name .href="${this.binHref}"></bin-path-name></h3>

        <div class="banner banner--info">
          <p class="description">
            Make sure you are at the right bin.
          </p>
        </div>
      </div>

      <div class="content">
        <bn-surface class="location">
          <h3>Location</h3>
          <work-order-location-details .href="${this.binHref}"></work-order-location-details>

          <bin-location .href="${this.binHref}"></bin-location>
        </bn-surface>

        <bn-surface>
          <h3>Photo</h3>
          <div class="photo"><img src="${this.photoHref}" /></div>
        </bn-surface>
      </div>

      ${this.replacing ? this._renderReplace() : this._renderScan()}

      ${renderIf(this.canSkip, () => html`
        <div class="skip">
          <button class="button button--plain" @click="${this._skip}">Skip</button>
        </div>
      `)}
    `;
  }

  _renderScan() {
    return html`
      <bn-surface>
        <h3>Scan Bin Sticker QR Code</h3>

        <p class="description">
          Scan the BinSentry Bin QR code sticker to start the work order.
        </p>

        <bn-flash></bn-flash>

        <qr-scanner
          @data="${this._parseBinQr}"
          @error="${(e) => this._error(e.detail.error.message)}"
        ></qr-scanner>
      </bn-surface>

      <bn-surface>
        <h3>Can't Find BinSentry QR Code Sticker</h3>

        <p class="description">
          If you can't find the BinSentry QR code sticker, and you're sure you're at the correct bin, attach a new sticker.
        </p>

        <button class="button button--tertiary" @click="${() => this.replacing = true}">Replace Sticker</button>
      </bn-surface>
    `;
  }

  _renderReplace() {
    return html`
      <bn-surface>
        <h3>Replace Bin Sticker</h3>

        <p class="subdued">
          Place a BinSentry QR code sticker on the bin in a location that is clearly visible.
        </p>

        <p class="subdued">
          Use your mobile device's camera to scan the QR code located on the BinSentry sticker.
        </p>

        <p>
          <strong>
            Warning:
            The previous BinSentry sticker will no longer be linked to this bin.
          </strong>
        </p>

        <bn-flash></bn-flash>

        <qr-scanner
          @data="${this._parseBinQr}"
          @error="${(e) => this._error(e.detail.error.message)}"
        ></qr-scanner>

        <div>
          <button class="button button--outlined" @click="${() => this.replacing = false}">Cancel</button>
        </div>
      </bn-surface>
    `;
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    if (this.getProperty('requestType') === 'installation') {
      this._next();
      return;
    }
    this.binHref = this.getHref('https://api.binsentry.com/rel/bin');
  }

  _onUpdateBin(e) {
    const { entity } = e.detail;

    this.serialNumber = entity.properties.serialNumber;

    const photos = entity && entity.getSubEntitiesByRel('https://api.binsentry.com/rel/bin-photo');
    this.photoHref = photos?.length && photos.pop().href;
  }

  _next() {
    store.dispatch(storeActions.navigate('/work-orders/show', { workOrderId: this.href }));
  }

  _skip() {
    confirm('By skipping this step, you risk being at the wrong bin')
      && this._next();
  }

  _clear() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
  }

  _success(msg) {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    flash.success(msg);
  }

  _error(error) {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    flash.critical(error);
  }

  async _match(serialNumber) {
    if (serialNumber === this.serialNumber) {
      this._success('You are at the correct bin.');
      setTimeout(() => this._next(), 1000);
    } else {
      this._error(html`
        <strong>Incorrect Bin Barcode</strong>
        <p>
          You are at the wrong bin.
          Expected '${this.serialNumber}' but found '${serialNumber}'.
        </p>
      `);
    }
  }

  async _save(serialNumber) {
    try {
      const action = this.entity.getActionByName('add-serial-number');
      action.getFieldByName('serialNumber').value = serialNumber;
      await this.submitAction(action);
      entityStore.invalidateHref(this.href);
      entityStore.invalidateHref(this.binHref);
      this.serialNumber = serialNumber;
      this._success('The BinSentry Sticker has been successfully linked to the bin.');
      setTimeout(() => this._next(), 1000);
    } catch (error) {
      this._error(html`
        There was a problem linking the BinSentry serial number to the bin.
        <div><code>${error.message}</code></div>
      `);
    }
  }

  async _parseBinQr(e) {
    const { data } = e.detail;
    if (this.processing || !data) {
      return;
    }
    try {
      this.processing = true;
      const url = new URL(data);

      if (!url.hostname.includes('binsentry.com')) {
        throw new Error('Unsupported hostname.');
      }

      if (!url.pathname.startsWith('/bins')) {
        throw new Error('Incorrect path name.');
      }

      const serialNumber = url.searchParams.get('serialNumber');

      if (this.replacing) {
        if (this.serialNumber === serialNumber) {
          // serial number hasn't changed
          return;
        }
        await this._save(serialNumber);
      } else {
        await this._match(serialNumber);
      }
    } catch (ex) {
      this._error(html`
        The QR code scanned does not appear to be for a BinSentry bin serial number.
        <div><code>${ex.message}</code></div>
      `);
    } finally {
      this.processing = undefined;
    }
  }

  _stateChanged({ organizationType }) {
    this.canSkip = organizationType === 'binsentry';
  }
}

customElements.define('work-order-bin-confirm', WorkOrderBinConfirm);
